/**
 * Чат с агентом «Архитектор шаблонов» (tools KLS / шаблоны).
 * Требуются права ai.chat.use и templates.manage.
 */

import { useState, useRef, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { Sparkles, Send, Loader2, ShieldCheck } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useAuth } from "@/hooks/useAuthV2";
import { aiAgentsUserApi } from "@/services/ai-agents-user.service";
import { useToast } from "@/hooks/use-toast";

interface Msg {
  role: "user" | "assistant" | "system";
  content: string;
  ts: Date;
}

export default function TemplateArchitectChatPanel() {
  const { hasAllPermissions } = useAuth();
  const { toast } = useToast();
  const canUse = hasAllPermissions(["ai.chat.use", "templates.manage"]);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<Msg[]>([]);
  const [pendingNonce, setPendingNonce] = useState<string | null>(null);
  const [pendingTool, setPendingTool] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const chatMut = useMutation({
    mutationFn: (text: string) => aiAgentsUserApi.templateArchitectChat(text),
    onSuccess: (data) => {
      setPendingNonce(data.meta?.pending_approval?.nonce ?? null);
      setPendingTool(data.meta?.pending_approval?.tool ?? null);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: data.message || "—",
          ts: new Date(),
        },
      ]);
      setInput("");
    },
    onError: (e: Error) => {
      toast({ title: "Ошибка ассистента", description: e.message, variant: "destructive" });
    },
  });

  const approveMut = useMutation({
    mutationFn: (nonce: string) => aiAgentsUserApi.templateArchitectApprove(nonce),
    onSuccess: (data) => {
      setPendingNonce(null);
      setPendingTool(null);
      if (!data.ok) {
        toast({
          title: "Подтверждение не выполнено",
          description: data.error ?? "Неизвестная ошибка",
          variant: "destructive",
        });
        return;
      }
      const summary = JSON.stringify(data.result ?? {}, null, 2).slice(0, 4000);
      setMessages((prev) => [
        ...prev,
        {
          role: "system",
          content: `Операция «${data.tool ?? "?"}» выполнена после подтверждения.\n${summary}`,
          ts: new Date(),
        },
      ]);
      toast({ title: "Операция подтверждена" });
    },
    onError: (e: Error) => {
      toast({ title: "Ошибка", description: e.message, variant: "destructive" });
    },
  });

  const send = () => {
    const t = input.trim();
    if (!t || !canUse || chatMut.isPending) return;
    setMessages((prev) => [...prev, { role: "user", content: t, ts: new Date() }]);
    chatMut.mutate(t);
  };

  if (!canUse) {
    return (
      <Alert>
        <AlertTitle>Нет доступа к ассистенту</AlertTitle>
        <AlertDescription>
          Нужны разрешения <code className="text-xs">ai.chat.use</code> и{" "}
          <code className="text-xs">templates.manage</code>.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="flex min-h-[min(70vh,520px)] flex-col gap-3">
      <div className="flex items-start gap-2 rounded-lg border bg-muted/30 p-3 text-sm">
        <Sparkles className="h-5 w-5 shrink-0 text-primary mt-0.5" />
        <div className="space-y-1 text-muted-foreground">
          <p className="text-foreground font-medium">Архитектор шаблонов</p>
          <p>
            Задавайте вопросы на естественном языке: ассистент обращается к справочникам KLS, списку шаблонов и
            инструментам проектирования карточек. Разрушительные действия требуют кнопки подтверждения ниже.
          </p>
        </div>
      </div>

      {pendingNonce && (
        <Alert className="border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800">
          <ShieldCheck className="h-4 w-4" />
          <AlertTitle>Ожидается подтверждение</AlertTitle>
          <AlertDescription className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-xs">
              Инструмент: <code className="font-mono">{pendingTool ?? "?"}</code>
            </span>
            <Button
              type="button"
              size="sm"
              variant="default"
              disabled={approveMut.isPending}
              onClick={() => approveMut.mutate(pendingNonce)}
            >
              {approveMut.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <ShieldCheck className="h-4 w-4 mr-1" />
              )}
              Подтвердить операцию
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <div className="min-h-[200px] flex-1 overflow-y-auto rounded-md border bg-background p-3 space-y-3">
        {messages.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">
            Например: «Какие типы виджетов полей доступны?» или «Покажи черновики шаблонов для организации X».
          </p>
        ) : (
          messages.map((m, i) => (
            <div
              key={i}
              className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[95%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-primary text-primary-foreground"
                    : m.role === "system"
                      ? "bg-muted border text-xs font-mono"
                      : "bg-muted"
                }`}
              >
                {m.content}
              </div>
            </div>
          ))
        )}
        <div ref={endRef} />
      </div>

      <div className="flex gap-2 items-end">
        <Textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Сообщение архитектору…"
          className="min-h-[72px] resize-none"
          disabled={chatMut.isPending}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
        />
        <Button
          type="button"
          size="icon"
          className="h-10 w-10 shrink-0"
          disabled={!input.trim() || chatMut.isPending}
          onClick={send}
        >
          {chatMut.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
        </Button>
      </div>
    </div>
  );
}
